import React, { useEffect, useState } from "react";
import { download, get, put } from "../lib/api";
import { useLoad } from "../lib/hooks";
import { parseKeywords } from "../lib/reports";
import { Badge, Button, Card, Checkbox, Confirm, ErrorBox, Loading, Segment, Spinner, Textarea } from "../ui";
import { useShell } from "../shell/context";

function KeywordList() {
    const { toast } = useShell();
    const { data, error, loading, reload, setData } = useLoad(() => get("v1/settings/privacy"), []);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);

    useEffect(() => {
        if (data) setText((data.special_category_keywords || []).join("\n"));
    }, [data]);

    if (loading && !data) return <Card><Loading /></Card>;
    if (error && !data) return <ErrorBox error={error} onRetry={reload} />;

    const list = parseKeywords(text);
    const dirty = JSON.stringify(list) !== JSON.stringify(data.special_category_keywords || []);

    const save = async (keywords) => {
        setBusy(true);
        try {
            const res = await put("v1/settings/privacy", { special_category_keywords: keywords });
            setData(res);
            toast(keywords === null ? "Default keywords restored." : "Keywords saved.");
            setConfirmReset(false);
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card
            title="Sensitive-data warning"
            actions={data.is_default ? <Badge color="secondary">Default list</Badge> : <Badge color="info">Edited</Badge>}
            footer={
                <div className="hz-row">
                    <span className="hz-spacer hz-small hz-muted">{list.length} keywords</span>
                    {!data.is_default && <Button variant="outline" disabled={busy} onClick={() => setConfirmReset(true)}>Restore defaults</Button>}
                    <Button variant="light" disabled={!dirty || busy} onClick={() => setText((data.special_category_keywords || []).join("\n"))}>Reset</Button>
                    <Button variant="primary" disabled={!dirty || busy} onClick={() => save(list)}>{busy && <Spinner />}Save</Button>
                </div>
            }
        >
            <div className="hz-muted" style={{ marginBottom: 12 }}>
                When a person's notes or a touch's text contain one of these words, the save still goes through and a notice suggests keeping only what is needed
                (health, religion, politics, ethnicity, sexual orientation, union membership, criminal records). Matching ignores case and accents and takes whole
                words; end a word with <code>*</code> to match its beginning (<code>pregnan*</code>). One keyword or phrase per line.
            </div>
            <Textarea rows={14} value={text} onChange={(e) => setText(e.target.value)} aria-label="Keywords" className="hz-mono" />
            <Confirm open={confirmReset} title="Restore default keywords" confirmLabel="Restore" busy={busy} onConfirm={() => save(null)} onClose={() => setConfirmReset(false)}>
                Your edits to the list are replaced by the built-in English and Turkish keywords.
            </Confirm>
        </Card>
    );
}

function FullExport() {
    const { toast } = useShell();
    const [format, setFormat] = useState("both");
    const [audit, setAudit] = useState(true);
    const [all, setAll] = useState(false);
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const name = await download("v1/crm/export", { format, audit, all }, "horus-export.zip");
            toast(`Downloaded ${name}.`, { icon: "ri-download-2-line" });
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setBusy(false);
        }
    };
    return (
        <Card title="Export everything">
            <div className="hz-muted" style={{ marginBottom: 12 }}>
                One zip with every CRM table (live and in the trash), settings and Claude tokens (never their secrets). It holds personal data: keep it somewhere safe and delete it when done.
            </div>
            <div className="hz-row-wrap" style={{ gap: 16, marginBottom: 14 }}>
                <span className="hz-row" style={{ gap: 8 }}>
                    <span className="hz-small hz-muted">Format</span>
                    <Segment
                        value={format}
                        onChange={setFormat}
                        options={[
                            { value: "both", label: "JSON + CSV" },
                            { value: "json", label: "JSON" },
                            { value: "csv", label: "CSV" },
                        ]}
                    />
                </span>
                <Checkbox checked={audit} onChange={setAudit}>Include history (audit log)</Checkbox>
                <Checkbox checked={all} onChange={setAll}>Include Talent data</Checkbox>
            </div>
            <Button variant="primary" icon="ri-download-2-line" disabled={busy} onClick={run}>
                {busy && <Spinner />}
                {busy ? "Preparing…" : "Download export"}
            </Button>
        </Card>
    );
}

// Settings → Privacy (E9).
export default function PrivacySettings() {
    return (
        <div className="hz-stack">
            <KeywordList />
            <FullExport />
        </div>
    );
}
